import React, { useState } from 'react';
import axios from 'axios';
import './SaveTheDate.css';

const CANCEL_REASONS = [
  'I expected a feature that isn’t available',
  'Too expensive',
  'Technical issue / bug',
  'I only needed it for a one-off task',
  'Not ready to plan yet',
  'Found another tool',
  'Other',
];

export default function CancellationRequest() {
  const [email, setEmail] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonOtherText, setCancelReasonOtherText] = useState('');
  const [expectedFeature, setExpectedFeature] = useState('');
  const [desiredFeature, setDesiredFeature] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const validate = () => {
    const errors = [];

    if (!email.trim()) {
      errors.push('Email is required.');
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        errors.push('Please enter a valid email address.');
      }
    }

    if (!cancelReason) {
      errors.push('Please select a reason for cancelling.');
    }

    if (
      cancelReason === 'I expected a feature that isn’t available' &&
      !expectedFeature.trim()
    ) {
      errors.push('Please tell us which feature you were expecting.');
    }

    if (cancelReason === 'Other' && !cancelReasonOtherText.trim()) {
      errors.push('Please tell us briefly why you are cancelling.');
    }

    const maxLen = 200;
    if (cancelReasonOtherText.trim().length > maxLen) {
      errors.push('"Other" detail must be 200 characters or fewer.');
    }
    if (expectedFeature.trim().length > maxLen) {
      errors.push('"What feature were you expecting?" must be 200 characters or fewer.');
    }
    if (desiredFeature.trim().length > maxLen) {
      errors.push('"What feature would you like to see?" must be 200 characters or fewer.');
    }

    if (errors.length > 0) {
      setErrorMessage(errors[0]);
      return false;
    }

    setErrorMessage('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage('');

    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const payload = {
        email: email.trim(),
        cancelReason,
        cancelReasonOtherText: cancelReasonOtherText.trim(),
        expectedFeature: expectedFeature.trim(),
        desiredFeature: desiredFeature.trim(),
        companyWebsite: companyWebsite || '',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      };

      const res = await axios.post(
        `${process.env.REACT_APP_API}/system/cancellation-request`,
        payload
      );

      if (res.data && res.data.ok) {
        setSuccessMessage('Thanks — we’ve received your request.');
        setEmail('');
        setCancelReason('');
        setCancelReasonOtherText('');
        setExpectedFeature('');
        setDesiredFeature('');
        setCompanyWebsite('');
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      if (error.response?.status === 400 && Array.isArray(error.response.data?.errors)) {
        setErrorMessage(error.response.data.errors[0]);
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const showOtherInput = cancelReason === 'Other';

  return (
    <div className="container">
      <div
        className="preview"
        style={{
          maxWidth: 560,
          width: '100%',
          margin: '40px auto 0',
          padding: '32px 24px',
          backgroundColor: '#ffffff',
          boxShadow: 'none',
          backgroundImage: 'none',
        }}
      >
        <h1
          style={{
            fontFamily: 'Noto Serif',
            fontSize: 32,
            color: '#323232',
            marginBottom: 10,
            textAlign: 'left',
          }}
        >
          Cancellation Request
        </h1>
        <p
          style={{
            fontFamily: 'Albert Sans, sans-serif',
            fontSize: 15,
            color: '#4D8495',
            textAlign: 'left',
            marginBottom: 25,
          }}
        >
          Submit this form and we’ll process your cancellation. This takes about a minute.
        </p>

        {successMessage && (
          <div
            style={{
              backgroundColor: '#e5f6ee',
              borderRadius: 8,
              padding: '10px 12px',
              marginBottom: 16,
              fontSize: 14,
              color: '#145c3a',
              fontFamily: 'Albert Sans, sans-serif',
            }}
          >
            {successMessage}
            <p style={{ marginTop: 6, fontSize: 13 }}>
              We’ll contact you at the email provided.
            </p>
          </div>
        )}

        {errorMessage && (
          <div
            style={{
              backgroundColor: '#fdecea',
              borderRadius: 8,
              padding: '10px 12px',
              marginBottom: 16,
              fontSize: 14,
              color: '#611a15',
              fontFamily: 'Albert Sans, sans-serif',
            }}
          >
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          {/* Honeypot – hidden from real users */}
          <div style={{ display: 'none' }}>
            <label htmlFor="companyWebsite">Company Website</label>
            <input
              id="companyWebsite"
              name="companyWebsite"
              type="text"
              value={companyWebsite}
              onChange={(e) => setCompanyWebsite(e.target.value)}
              autoComplete="off"
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label
              htmlFor="email"
              style={{
                display: 'block',
                fontFamily: 'Albert Sans, sans-serif',
                fontSize: 14,
                marginBottom: 6,
                color: '#323232',
              }}
            >
              Email address*
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid #B0ADAD',
                fontFamily: 'Albert Sans, sans-serif',
                fontSize: 14,
              }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <p
              style={{
                fontFamily: 'Albert Sans, sans-serif',
                fontSize: 14,
                marginBottom: 8,
                color: '#323232',
              }}
            >
              Why are you cancelling?*
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {CANCEL_REASONS.map((reason) => (
                <label
                  key={reason}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    fontFamily: 'Albert Sans, sans-serif',
                    fontSize: 14,
                    color: '#323232',
                  }}
                >
                  <input
                    type="radio"
                    name="cancelReason"
                    value={reason}
                    checked={cancelReason === reason}
                    onChange={() => setCancelReason(reason)}
                  />
                  <span>{reason}</span>
                </label>
              ))}
            </div>
          </div>

          {showOtherInput && (
            <div style={{ marginBottom: 16 }}>
              <label
                htmlFor="otherDetail"
                style={{
                  display: 'block',
                  fontFamily: 'Albert Sans, sans-serif',
                  fontSize: 14,
                  marginBottom: 6,
                  color: '#323232',
                }}
              >
                Please tell us briefly*
              </label>
              <input
                id="otherDetail"
                type="text"
                maxLength={200}
                value={cancelReasonOtherText}
                onChange={(e) => setCancelReasonOtherText(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #B0ADAD',
                  fontFamily: 'Albert Sans, sans-serif',
                  fontSize: 14,
                }}
              />
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label
              htmlFor="expectedFeature"
              style={{
                display: 'block',
                fontFamily: 'Albert Sans, sans-serif',
                fontSize: 14,
                marginBottom: 6,
                color: '#323232',
              }}
            >
              What feature were you expecting
              {cancelReason === 'I expected a feature that isn’t available' ? '*' : ''}
              ?
            </label>
            <input
              id="expectedFeature"
              type="text"
              maxLength={200}
              value={expectedFeature}
              onChange={(e) => setExpectedFeature(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid #B0ADAD',
                fontFamily: 'Albert Sans, sans-serif',
                fontSize: 14,
              }}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label
              htmlFor="desiredFeature"
              style={{
                display: 'block',
                fontFamily: 'Albert Sans, sans-serif',
                fontSize: 14,
                marginBottom: 6,
                color: '#323232',
              }}
            >
              What feature would you like to see?
            </label>
            <input
              id="desiredFeature"
              type="text"
              maxLength={200}
              value={desiredFeature}
              onChange={(e) => setDesiredFeature(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid #B0ADAD',
                fontFamily: 'Albert Sans, sans-serif',
                fontSize: 14,
              }}
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              width: '100%',
              padding: '12px 20px',
              borderRadius: 999,
              border: 'none',
              backgroundColor: isSubmitting ? '#B0ADAD' : '#4D8495',
              color: '#fff',
              fontFamily: 'Albert Sans, sans-serif',
              fontSize: 15,
              fontWeight: 600,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s ease',
            }}
          >
            {isSubmitting ? 'Submitting…' : 'Submit cancellation request'}
          </button>
        </form>

        <p
          style={{
            marginTop: 16,
            fontSize: 12,
            color: '#777',
            fontFamily: 'Albert Sans, sans-serif',
            textAlign: 'center',
          }}
        >
          {/* Linked from account cancellation UI */}
        </p>
      </div>
    </div>
  );
}

